/**
 * PD-35243 — Flush pending PostHog web-vitals on page unload events.
 *
 * INP and CLS are finalized late in the page lifecycle and PostHog's internal
 * web-vitals reporter buffers them until the metric observer fires. On mobile,
 * hard navigations and tab-switches fire `pagehide` / `visibilitychange` before
 * the buffered metrics are sent. Calling `posthog.capture` (via `sendBeacon`
 * transport which PostHog uses automatically in unload paths) at that moment
 * ensures the metrics reach the ingestion API even on iOS Safari where
 * `beforeunload` does not fire.
 *
 * PostHog already enriches every event with `$device_type`, `$current_url`, and
 * `$pathname` super-properties automatically. We duplicate them explicitly in the
 * flush marker event so the event is self-contained for segmentation in Insights.
 */

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { buildWebVitalsFlushProperties } from 'utils/webVitals';

const useWebVitalsFlush = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const flush = (reason: 'pagehide' | 'visibilitychange') => {
      try {
        if (
          posthog &&
          typeof posthog.__loaded !== 'undefined' &&
          posthog.__loaded &&
          typeof posthog.capture === 'function'
        ) {
          const props = buildWebVitalsFlushProperties(
            navigator.userAgent,
            window.location.href,
            window.location.pathname,
            reason,
          );
          posthog.capture('web_vitals_flush', props);
        }
      } catch {
        // Silently swallow — flush is best-effort; never crash the unload path.
      }
    };

    const handlePageHide = () => flush('pagehide');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush('visibilitychange');
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};

export default useWebVitalsFlush;
